import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Plus, RefreshCw, Search, Edit, Trash2, Bot } from 'lucide-react'
import { toast } from 'react-toastify'
import { agentsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import AgentModal from '../components/AgentModal'
import ConfirmDialog from '../components/ConfirmDialog'

function Agents() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteAgent, setDeleteAgent] = useState(null)
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: agents, isLoading } = useQuery('agents', agentsAPI.getAgents)
  
  const syncMutation = useMutation(agentsAPI.syncAgents, {
    onSuccess: (data) => {
      toast.success(data.data.message)
      queryClient.invalidateQueries('agents')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Sync failed')
    }
  })

  const deleteMutation = useMutation(agentsAPI.deleteAgent, {
    onSuccess: () => {
      toast.success('Agent deleted successfully')
      queryClient.invalidateQueries('agents')
      setDeleteAgent(null)
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Delete failed')
    }
  })

  const filteredAgents = agents?.data?.agents?.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  const handleEdit = (agent) => {
    setSelectedAgent(agent)
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setSelectedAgent(null)
    setIsModalOpen(true)
  }

  const handleDelete = (agent) => {
    setDeleteAgent(agent)
  }

  const confirmDelete = () => {
    if (deleteAgent) {
      deleteMutation.mutate(deleteAgent._id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-900">AI Agents</h1>
          <p className="mt-1 text-sm text-primary-600">
            Manage your conversational AI agents
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-3">
          {user?.role === 'admin' && (
            <>
              <button
                type="button"
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isLoading}
                className="btn-secondary"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {syncMutation.isLoading ? 'Syncing...' : 'Sync Data'}
              </button>
              <button
                type="button"
                onClick={handleCreate}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Agent
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-primary-400" />
          </div>
          <input
            type="text"
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Agents Grid */}
      {filteredAgents.length === 0 ? (
        <div className="text-center py-12">
          <Bot className="mx-auto h-12 w-12 text-primary-400" />
          <h3 className="mt-2 text-sm font-medium text-primary-900">No agents</h3>
          <p className="mt-1 text-sm text-primary-500">
            {searchTerm ? 'No agents match your search.' : 'Get started by creating a new agent.'}
          </p>
          {user?.role === 'admin' && !searchTerm && (
            <div className="mt-6">
              <button
                type="button"
                onClick={handleCreate}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Agent
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredAgents.map((agent) => (
            <div key={agent._id} className="card group hover:shadow-md transition-shadow">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <Bot className="h-5 w-5 text-primary-600" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-primary-900 truncate">
                        {agent.name}
                      </p>
                      <p className="text-sm text-primary-500 truncate">
                        {agent.voiceName || 'No voice assigned'}
                      </p>
                    </div>
                  </div>
                  <span className={`status-badge ${agent.isActive ? 'status-active' : 'status-inactive'}`}>
                    {agent.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {agent.description && (
                  <p className="mt-3 text-sm text-primary-600 line-clamp-2">
                    {agent.description}
                  </p>
                )}

                <div className="mt-4 space-y-2">
                  {agent.language && (
                    <div className="text-xs text-primary-500">
                      Language: {agent.language.toUpperCase()}
                    </div>
                  )}
                  {agent.lastSyncedAt && (
                    <div className="text-xs text-primary-500">
                      Last synced: {new Date(agent.lastSyncedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {user?.role === 'admin' && (
                  <div className="mt-4 flex justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEdit(agent)}
                      className="p-1 text-primary-600 hover:text-primary-900"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(agent)}
                      className="p-1 text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AgentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        agent={selectedAgent}
      />

      <ConfirmDialog
        isOpen={!!deleteAgent}
        onClose={() => setDeleteAgent(null)}
        onConfirm={confirmDelete}
        title="Delete Agent"
        message={`Are you sure you want to delete "${deleteAgent?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="btn-danger"
      />
    </div>
  )
}

export default Agents